import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import * as Yup from 'yup';
import { useFormik, FormikErrors } from 'formik';
import { Button, Checkbox, Input, Alert } from '@/shared/ui';
import { OnboardingController } from '@/shared/endpoints/onboarding';
import AutoAddress from '@/modules/onboarding/pages/about-you/address/components/AutoAddress';
import ManualAddressFormElements from '../manual-address-form-elements';
import { AddressValidation } from './types';
import { ExtendedAddress } from './types';
import { Address } from '@/shared/endpoints/onboarding/models/response';
import { AddressTemplate } from './components/AddressTemplate';
import { ICustomersUserAddressesResponse } from '../../endpoints/customers';
import { useHeaderHeight } from '@/shared/components/header-height-context';

import { onlyCharsAndNumbers, charLimitBetweenFourAndTwenty } from '../../constants/regex';
const labelSchema = Yup.string()
    .trim()
    .required('Please enter a label.')
    .matches(charLimitBetweenFourAndTwenty, 'Label must be between 4 and 20 characters.')
    .matches(onlyCharsAndNumbers, 'Label can only contain letters and numbers.');

const validationSchema = Yup.object({
    addressLine1: Yup.string().trim().required('Please enter a street address.'),
    city: Yup.string().trim().required('Please enter a city.'),
    state: Yup.string().trim().length(2, 'State must be exactly 2 characters.').required('Please enter a state.'),
    zip: Yup.string().test('len', 'Please enter a valid zip code', val => {
        return val?.toString().length === 5;
    }),
    label: labelSchema,
    unit: Yup.string().test('required-if-needsUnit', 'Unit is required field', function () {
        const { needsUnit, unit } = this.parent;
        return !needsUnit || Boolean(unit);
    })
});

const emptyCoordinates = {
    lat: '',
    lng: ''
};

const selectedAddressDefaultValue: ExtendedAddress = {
    fullAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    unit: '',
    verified: false,
    needsUnit: false,
    label: '',
    preferred: false
};

const capitalizeWords = (text: string) => {
    if (!text) return '';
    return text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export interface IProps {
    loading?: boolean;
    autoDefault?: boolean;
    manualFormTitle?: string;
    visibleBackToAuto?: boolean;
    withDefaultCheckbox?: boolean;
    withManualAddressDemo?: boolean;
    withAutoAddressPreview?: boolean;
    defaultLabelAndHidden?: string;
    feValidation?: { field: string; message: string } | null;
    formVariant?: AddressFormVariant;
    defaultValues?: Partial<ICustomersUserAddressesResponse>;
    onSubmit?: (address: ExtendedAddress, formVariant: AddressFormVariant, skipApi: boolean) => void | Promise<void>;
    onVariantChange?: (variant: 'auto' | 'manual') => void;
    handleEdit?: () => void;
    onGoBack?: () => void;
    resetFeValidation?: () => void;
}
const basicFailure = 'Unable to verify address. Please review and make sure you are entering a valid address.';
const unableToUseAutoCompleteError = 'Unable to verify address. Please enter your address manually.';
const addressVerificationFailedError = 'Address verification failed. Please provide more details.';

const getFullAddressString = (address: Address) => {
    const { streetAddress, city, state, ZIPCode } = address;

    let fullAddress = '';

    if (streetAddress) fullAddress += capitalizeWords(streetAddress);
    if (city) fullAddress += `${fullAddress ? ', ' : ''}${capitalizeWords(city)}`;
    if (state) fullAddress += `${fullAddress ? ', ' : ''}${state.toUpperCase()}`;
    if (ZIPCode) fullAddress += `${fullAddress ? ' ' : ''}${ZIPCode}`;

    return fullAddress;
};

export type AddressFormVariant = 'auto' | 'manual';

const MainAddressForm = ({
    autoDefault = false,
    loading: propLoading,
    manualFormTitle = '',
    visibleBackToAuto,
    withDefaultCheckbox,
    defaultValues = {},
    formVariant: propFormVariant = 'auto',
    defaultLabelAndHidden = '',
    withManualAddressDemo,
    withAutoAddressPreview,
    onSubmit,
    onVariantChange,
    onGoBack,
    feValidation,
    resetFeValidation
}: IProps) => {
    const [loading, setLoading] = useState(false);
    const [unitNeeded, setUnitNeeded] = useState(false);
    const [uniqueKey, setUniqueKey] = useState(0);
    const [defaultAddressValid, setDefaultAddressValid] = useState(!!defaultValues?.verified);
    const [selectedAddressState, setSelectedAddressState] = useState<AddressValidation>({ needsUnit: false, error: '' });
    const [formVariant, setFormVariant] = useState<AddressFormVariant>(propFormVariant);
    const [addressViewing, setAddressViewing] = useState<boolean>(false);
    const headerHeight = useHeaderHeight();

    const formik = useFormik<ExtendedAddress>({
        validationSchema,
        initialValues: {
            ...selectedAddressDefaultValue,
            ...defaultValues,
            preferred: autoDefault,
            label: defaultLabelAndHidden
        } as ExtendedAddress,
        onSubmit: () => {
            handleVerifyAddress(formik.values);
        }
    });

    const getAddressDefaults = useCallback(() => {
        return {
            ...selectedAddressDefaultValue,
            label: formik.values.label || '',
            preferred: autoDefault || formik.values.preferred
        };
    }, [autoDefault, formik.values.label, formik.values.preferred]);

    const handleManualInput = useCallback((keepError = false) => {
        formik.setValues(getAddressDefaults());
        formik.setTouched({});
        setFormVariant('manual');
        onVariantChange?.('manual');
        setUnitNeeded(false);
        formik.setFieldValue('needsUnit', false);
        !keepError && setSelectedAddressState({ needsUnit: false, error: '' });
    }, [formik, getAddressDefaults, onVariantChange]);

    const handleVerifyAddress = useCallback(
        async (values: ExtendedAddress, reqAuto?: boolean) => {
            setLoading(true);
            const address = {
                addressLine2: values.addressLine1 || '',
                addressLine1: values.unit || '',
                city: values.city,
                state: values.state,
                zip: values.zip
            };

            try {
                const res = await OnboardingController.validateAddress(address);
                if (res?.error || (res.corrections || []).some(correction => correction.text)) {
                    if (res?.error) {
                        setSelectedAddressState({ needsUnit: false, error: formVariant === 'manual' ? basicFailure : unableToUseAutoCompleteError });
                        setFormVariant('manual');
                        onVariantChange?.('manual');
                        handleManualInput(true);
                    } else {
                        const needsUnit = (res.corrections || []).some(correction => correction.code === '32' && correction.text);
                        if (values.unit && needsUnit) {
                            formik.setFieldError('unit', 'Please enter a valid unit');
                        } else {
                            // the needs unit without having added the unit yet case
                            setSelectedAddressState({ needsUnit, error: addressVerificationFailedError });
                            setUnitNeeded(needsUnit);
                            formik.setFieldValue('needsUnit', needsUnit);
                            formik.setTouched({ needsUnit: false });
                        }
                    }
                } else {
                    const fullAddress = getFullAddressString(res.address!);
                    // in the case of an automatic address, we need to validate the label to prevent submitting a form                    
                    try {
                        await labelSchema.validate(values.label);
                    } catch (error) {
                        formik.setFieldError('label', 'Please enter a label');
                        return;
                    }

                    const newValues: ExtendedAddress = {
                        ...values,
                        addressLine1: res?.address?.streetAddress || '',
                        addressLine2: '',
                        city: res?.address?.city || '',
                        state: res?.address?.state || '',
                        zip: res?.address?.ZIPCode || '',
                        unit: res?.address?.secondaryAddress || '',
                        fullAddress,
                        verified: true,
                        preferred: values.preferred,
                        label: values.label
                    };


                    if (reqAuto) {
                        onSubmit?.(newValues, formVariant, false);
                    } else {
                        if (formVariant === 'auto' || (!withManualAddressDemo && formVariant === 'manual')) {
                            const ok = await onSubmit?.(newValues, formVariant, false);
                            if (ok && withAutoAddressPreview) setDefaultAddressValid(true);
                        } else {
                            handleAddressSelection(newValues, false);
                            setAddressViewing(true);
                        }
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
                setUniqueKey(prevKey => prevKey + 1);
            }
        },
        [onVariantChange, handleManualInput, formik, onSubmit, formVariant, withManualAddressDemo]
    );

    const handleAddressSelection = useCallback(
        (address: ExtendedAddress, autoVerify = true) => {
            const entries = Object.entries(address);
            const promises: Promise<void> | Promise<FormikErrors<ExtendedAddress>>[] = [];
            entries.forEach(([key, value]) => {
                promises.push(Promise.resolve(formik.setFieldValue(key, value, false) as Promise<FormikErrors<ExtendedAddress>>));
            });
            Promise.all(promises).then(() => {
                autoVerify && handleVerifyAddress({ ...address, preferred: formik.values.preferred });
            });
        },
        [formik, handleVerifyAddress, resetFeValidation]
    );

    const handleAdditionalAddressChange = useCallback(
        (value: string) => {
            resetFeValidation?.();
            formik.setValues(prevValues => ({
                ...prevValues,
                verified: false,
                unit: value
            }));
        },
        [formik, resetFeValidation]
    );

    const handleAddressInputEvent = useCallback(() => {
        setSelectedAddressState({ needsUnit: false, error: '' });
        formik.setValues(getAddressDefaults());
        setUnitNeeded(false);
        formik.setFieldTouched('needsUnit', false);
        formik.setFieldValue('needsUnit', false);
        resetFeValidation?.();
    }, [formik, getAddressDefaults, resetFeValidation]);

    const handleManualFieldChange = useCallback(
        (field: string, value: string) => {
            resetFeValidation?.();
            if (field !== 'unit') {
                formik.setValues({
                    ...formik.values,
                    [field]: value,
                    unit: '',
                    needsUnit: false
                });
                setUnitNeeded(false);
                setSelectedAddressState(() => ({
                    error: '',
                    needsUnit: false
                }));
            } else {
                formik.setFieldValue(field, value);
            }
        },
        [formik, resetFeValidation]
    );

    const handleBackToAuto = useCallback(() => {
        setFormVariant('auto');
        onVariantChange?.('auto');
        formik.setValues(getAddressDefaults());
        setUnitNeeded(false);
        setSelectedAddressState(() => ({
            error: '',
            needsUnit: false
        }));
        formik.setTouched({});
    }, [formik, getAddressDefaults, onVariantChange]);

    const handleTryAgain = useCallback(() => {
        setAddressViewing(false);
        if (feValidation?.field === 'address' || !feValidation) {
            formik.setValues(getAddressDefaults());
            formik.setTouched({});
            setUnitNeeded(false);
            setSelectedAddressState(() => ({
                error: '',
                needsUnit: false
            }));
        }
    }, [feValidation, formik, getAddressDefaults]);

    const handleEdit = () => {
        setDefaultAddressValid(false);
    };

    useEffect(() => {
        resetFeValidation?.()
    }, [formik.values.label, resetFeValidation])

    useEffect(() => {
        if (addressViewing && feValidation) {
            handleTryAgain()
        }
    }, [addressViewing, feValidation, handleTryAgain]);

    const checkForErrorsAndScroll = useCallback(() => {
        const element = document.querySelector(`.alert__error, .border-red-500`);

        if (!element) return;
        const rect = element.getBoundingClientRect();
        const isVisible =
            rect.top >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

        if (isVisible) return;

        const elementPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight - 200;

        setTimeout(() => {
            window.scrollTo({
                top: elementPosition,
                behavior: 'smooth'
            });
        }, 200)
    }, [headerHeight])

    const onSubmitClick = useCallback((e: { preventDefault: () => void; }) => {
        e.preventDefault();
        formik.handleSubmit();
        setTimeout(() => {
            checkForErrorsAndScroll();
        }, 400);
    }, [checkForErrorsAndScroll, formik])

    console.log(selectedAddressState)

    return (
        <div className="flex flex-col" data-testid="MainAddressForm">
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column' }}>
                {addressViewing || defaultAddressValid ? (
                    <AddressTemplate
                        address={formik.values}
                        onSubmit={() => {
                            if (defaultAddressValid) {
                                onSubmit?.(formik.values, formVariant, true);
                            } else {
                                handleVerifyAddress(
                                    { ...formik.values, label: formik.values.label!.trim(), coordinates: emptyCoordinates },
                                    true
                                );
                            }
                        }}
                        defaultAddressValid={defaultAddressValid}
                        onEdit={handleEdit}
                        loading={loading || propLoading}
                        onGoBack={onGoBack}
                        tryAgainHandler={handleTryAgain}
                    />
                ) : (
                    <>
                        <div className="flex flex-col gap-4">
                            {formVariant === 'manual' ? (
                                <ManualAddressFormElements
                                    formTitle={manualFormTitle}
                                    handleChange={handleManualFieldChange}
                                    values={formik.values}
                                    errors={formik.errors}
                                    touched={formik.touched}
                                    selectedAddressState={selectedAddressState}
                                    error={
                                        !selectedAddressState.needsUnit &&
                                            selectedAddressState.error &&
                                            selectedAddressState.error === unableToUseAutoCompleteError
                                            ? selectedAddressState.error
                                            : ''
                                    }
                                    showApartmentSuiteBoxNumberField={unitNeeded}
                                />
                            ) : (
                                <AutoAddress
                                    key={uniqueKey}
                                    values={formik.values}
                                    touched={formik.touched}
                                    errors={formik.errors}
                                    needsUnit={unitNeeded}
                                    handleBlur={() => { }}
                                    handleManualInput={handleManualInput}
                                    onAddressSelection={handleAddressSelection}
                                    onAdditionalAddressChange={handleAdditionalAddressChange}
                                    onAddressInputEvent={handleAddressInputEvent}
                                />
                            )}
                            {!defaultLabelAndHidden && <Input
                                label="Address Label"
                                name="label"
                                type="text"
                                placeholder="ex. Home"
                                value={formik.values.label}
                                error={formik.touched.label && Boolean(formik.errors.label)}
                                errorMessage={formik.errors.label}
                                onChange={formik.handleChange}
                                required
                                testId="address-label"
                            />}
                            {withDefaultCheckbox && (
                                <Checkbox
                                    label={
                                        <span className="relative top-0 font-atkinsonHyperlegible text-base md:text-lg">
                                            Make this default shipping address
                                        </span>
                                    }
                                    name="preferred"
                                    checked={formik.values.preferred}
                                    onChange={formik.handleChange}
                                />
                            )}
                        </div>
                        {(feValidation || selectedAddressState.error === basicFailure) && <Alert className="mt-2.5 font-dmSans lg:mt-6" label={feValidation?.message || selectedAddressState.error} />}
                        <Button
                            // data-testid="save" TODO: find after
                            type="submit"
                            variant="contained"
                            color="primary"
                            showEndIcon
                            className="mt-6"
                            loading={loading || propLoading}
                            testId="button-address-form-continue"
                            onClick={onSubmitClick}
                        >
                            Save & Continue
                        </Button>

                        {(visibleBackToAuto || formVariant === 'manual') && (
                            <Button
                                sx={{ width: '100%', mt: 2 }}
                                variant="outlined"
                                disabled={loading || propLoading}
                                onClick={formVariant === 'manual' ? handleBackToAuto : onGoBack}
                                testId="button-address-form-back"
                            >
                                Back
                            </Button>
                        )}
                    </>
                )}
            </Box>
        </div>
    );
};

export default MainAddressForm;
